import {LogoutIcon, SettingsIcon} from "@components/icons";
import {UserAvatar} from "@components/UserAvatar";
import {RootState} from "@core/store";
import {Selectors} from "@core/store/selectors";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {translate} from "@util";
import {useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import {clearApp, setSettingsOpen} from "@core/store/slices/appSlice";
import {Settings} from "../settings";
import {LogoutDialog} from "../LogoutDialog";
import {logout} from "@core/store/slices/authSlice";
import {api, userApi} from "@core/api";
import {useDownBreakpoint} from "@hooks/useDownBreakpoint";
import {Profile} from "../profile";
import {ProfileData, ProfileDialog} from "../profile/ProfileDialog";
import {skipToken} from "@reduxjs/toolkit/query";

const UserAction = ({size}: {size?: "small" | "medium" | "large"}) => {
  const dispatch = useDispatch();

  const user = useSelector(Selectors.user);
  const settingsOpen = useSelector(
    (state: RootState) => state.app.settingsOpen,
  );

  const [logoutMutation, {isLoading: isLoggingOut}] = api.useLogoutMutation();
  const [update, {isLoading: isUpdateLoading}] =
    api.useUpdateUserInfoMutation();
  const {data: userDetail, refetch} = userApi.useFindEntityById(
    user?.id ?? skipToken,
  );

  const [profileDialog, setProfileDialog] = useState<boolean>(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleSave = (data: ProfileData) => {
    update(data)
      .unwrap()
      .then(() => {
        refetch();
        setProfileDialog(false);
      });
  };

  const getUAWidth = () => {
    switch (size) {
      case "small":
        return 28;
      case "medium":
        return 40;
      case "large":
        return 28;
    }
  };

  const getUAVariant = () => {
    switch (size) {
      case "small":
        return "caption";
      case "medium":
        return "body1";
      case "large":
        return "body1";
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    dispatch(setSettingsOpen(true));
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
    } catch {
      // Se l'API fallisce comunque facciamo il logout locale
    } finally {
      // Effettua il logout locale indipendentemente dal risultato dell'API
      dispatch(logout());
      dispatch(clearApp());
      dispatch(api.util.resetApiState());
      setLogoutDialogOpen(false);
    }
  };

  const isDownLg = useDownBreakpoint("lg");

  return (
    <>
      <IconButton sx={{p: 0}} onClick={handleMenuClick}>
        <UserAvatar
          id={user?.id}
          width={getUAWidth()}
          variant={getUAVariant()}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}>
        {isDownLg ? (
          <>
            <Profile
              minimize
              user={userDetail}
              onEdit={() => setProfileDialog(true)}
            />
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{translate("userActions.settings")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{translate("userActions.logout")}</ListItemText>
            </MenuItem>
          </>
        ) : (
          <Profile user={userDetail} onEdit={() => setProfileDialog(true)} />
        )}
      </Menu>

      <ProfileDialog
        data={{
          email: userDetail?.email,
          phone: userDetail?.phone,
          name: userDetail?.name,
          img: userDetail?.img,
          surname: userDetail?.surname,
        }}
        open={profileDialog}
        onSave={handleSave}
        onClose={() => setProfileDialog(false)}
        loading={isUpdateLoading}
      />
      <Settings
        open={settingsOpen}
        onClose={() => dispatch(setSettingsOpen(false))}
      />
      <LogoutDialog
        onConfirm={handleLogout}
        onCancel={() => setLogoutDialogOpen(false)}
        open={logoutDialogOpen}
        loading={isLoggingOut}
      />
    </>
  );
};

export {UserAction};
